import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CheckoutOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const CheckoutContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 30px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #333;
  }
`;

const Title = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 28px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin-bottom: 15px;
  font-size: 20px;
  border-bottom: 2px solid #ff6b6b;
  padding-bottom: 5px;
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #eee;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #f9f9f9;
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 15px;
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.h4`
  margin: 0 0 5px 0;
  color: #333;
`;

const ItemPrice = styled.p`
  margin: 0;
  color: #ff6b6b;
  font-weight: bold;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 16px;
  &:hover {
    background: #ff5252;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 5px;
`;

const RemoveButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
  margin-left: 10px;
  &:hover {
    background: #ff3742;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 15px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 5px;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  resize: vertical;
  min-height: 80px;
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const TotalSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin: 20px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 16px;
`;

const TotalAmount = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  font-weight: bold;
  color: #ff6b6b;
  border-top: 2px solid #ddd;
  padding-top: 10px;
  margin-top: 10px;
`;

const SubmitButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 15px 30px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  margin-top: 20px;
  &:hover {
    background: #ff5252;
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;



const StripeCheckoutForm = ({ cart, onUpdateCart, onRemoveFromCart, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    shippingMethod: 'STANDARD',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);

  const shippingMethods = {
    STANDARD: { name: 'Standard Shipping', cost: 5.99 },
    EXPRESS: { name: 'Express Shipping', cost: 12.99 }
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      let price = item.price;
      if (typeof price === 'string') {
        // Remove $ and any non-numeric characters except decimal point
        price = price.replace(/[^0-9.]/g, '');
      }
             const numericPrice = parseFloat(price) || 13.99;
      return sum + (numericPrice * item.quantity);
    }, 0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    onUpdateCart(itemId, newQuantity);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setOrderStatus(null);

    try {
      // Create Stripe Checkout Session
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cart: cart,
          customerInfo: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: {
              line1: formData.address1,
              line2: formData.address2,
              city: formData.city,
              state: formData.state,
              postal_code: formData.zip,
              country: formData.country
            }
          },
          shippingMethod: formData.shippingMethod,
          notes: formData.notes,
                     total: calculateSubtotal()
        })
      });

      const result = await response.json();

      if (response.ok && result.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.url;
      } else {
        throw new Error(result.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      setOrderStatus({
        type: 'error',
        message: error.message || 'An error occurred while creating checkout session'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <EmptyCart>
        <h3>Your cart is empty</h3>
        <p>Add some products to get started!</p>
      </EmptyCart>
    );
  }

  return (
    <>
      <Section>
        <SectionTitle>🛒 Your Cart</SectionTitle>
        {cart.map((item) => (
          <CartItem key={item.id}>
            <ItemImage src={item.image} alt={item.name} />
            <ItemDetails>
              <ItemName>{item.name}</ItemName>
              <ItemPrice>{item.price}</ItemPrice>
            </ItemDetails>
            <QuantityControl>
              <QuantityButton 
                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
              >
                -
              </QuantityButton>
              <QuantityInput
                type="number"
                value={item.quantity}
                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                min="1"
              />
              <QuantityButton 
                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
              >
                +
              </QuantityButton>
            </QuantityControl>
            <RemoveButton onClick={() => onRemoveFromCart(item.id)}>
              Remove
            </RemoveButton>
          </CartItem>
        ))}
      </Section>

             <TotalSection>
         <TotalRow>
           <span>Subtotal:</span>
           <span>${calculateSubtotal().toFixed(2)}</span>
         </TotalRow>
         <div style={{
           fontSize: '14px',
           color: '#666',
           textAlign: 'center',
           marginTop: '10px',
           padding: '10px',
           backgroundColor: '#f8f9fa',
           borderRadius: '5px',
           border: '1px solid #e9ecef'
         }}>
           🚚 Shipping will be calculated after you select your shipping method below
         </div>
         <TotalAmount>
           <span>Total:</span>
           <span>${calculateSubtotal().toFixed(2)}</span>
         </TotalAmount>
       </TotalSection>

      <Form onSubmit={handleSubmit}>
        <Section>
          <SectionTitle>📋 Shipping Information</SectionTitle>
          <FormGroup>
            <Label>Full Name *</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Email *</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Phone</Label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <Label>Address Line 1 *</Label>
            <Input
              type="text"
              name="address1"
              value={formData.address1}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Address Line 2</Label>
            <Input
              type="text"
              name="address2"
              value={formData.address2}
              onChange={handleInputChange}
            />
          </FormGroup>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <FormGroup>
              <Label>City *</Label>
              <Input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>State *</Label>
              <Input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <FormGroup>
              <Label>ZIP Code *</Label>
              <Input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Country *</Label>
              <Select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </Select>
            </FormGroup>
          </div>
        </Section>

        <Section>
          <SectionTitle>🚚 Shipping Method</SectionTitle>
          <FormGroup>
            <Select
              name="shippingMethod"
              value={formData.shippingMethod}
              onChange={handleInputChange}
              required
            >
              {Object.entries(shippingMethods).map(([key, method]) => (
                <option key={key} value={key}>
                  {method.name} - ${method.cost}
                </option>
              ))}
            </Select>
          </FormGroup>
        </Section>

                 <Section>
           <SectionTitle>💳 Payment</SectionTitle>
           <div style={{
             padding: '15px',
             backgroundColor: '#f8f9fa',
             borderRadius: '8px',
             border: '2px solid #ff6b6b',
             textAlign: 'center'
           }}>
             <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#333' }}>
               💳 Secure Payment Processing
             </p>
             <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
               You'll be redirected to Stripe's secure checkout page to complete your payment.
             </p>
           </div>
         </Section>

        <Section>
          <SectionTitle>📝 Order Notes</SectionTitle>
          <FormGroup>
            <TextArea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Any special instructions or notes for your order..."
            />
          </FormGroup>
        </Section>

        {orderStatus && (
          <div style={{
            padding: '15px',
            borderRadius: '8px',
            backgroundColor: orderStatus.type === 'success' ? '#d4edda' : '#f8d7da',
            color: orderStatus.type === 'success' ? '#155724' : '#721c24',
            marginBottom: '20px'
          }}>
            {orderStatus.message}
          </div>
        )}

                                                                       <SubmitButton type="submit" disabled={isSubmitting}>
             {isSubmitting ? 'Redirecting to Stripe...' : `Proceed to Payment - $${calculateSubtotal().toFixed(2)} + shipping`}
           </SubmitButton>
      </Form>
    </>
  );
};

const StripeCheckout = ({ isOpen, onClose, cart, onUpdateCart, onRemoveFromCart }) => {
  if (!isOpen) return null;

  return (
    <CheckoutOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <CheckoutContainer
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
      >
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <Title>🐔 Chikin Lady Checkout</Title>

        <StripeCheckoutForm
          cart={cart}
          onUpdateCart={onUpdateCart}
          onRemoveFromCart={onRemoveFromCart}
          onClose={onClose}
        />
      </CheckoutContainer>
    </CheckoutOverlay>
  );
};

export default StripeCheckout;
